/**
 * Free-text search across the API's list endpoints.
 *
 * Every list endpoint accepts two query parameters that together form a
 * case-insensitive "contains" search:
 *
 *  - `filterBy`     — semicolon separated string columns of the listed object
 *  - `filterByLike` — the text to look for, matched as `%text%` against each
 *                     of those columns and OR'd together
 *
 * The backend ignores any column the listed object does not have, and only
 * fails the request when none of the named columns exist — so a field list may
 * safely name columns that only some objects carry, as long as `name` is there.
 *
 * (`filterByValue` does the same as an exact match and `filterByNotLike` as a
 * negation, neither of which a search field needs.)
 */

/**
 * The columns each list searches over. Keep `name` first in every entry: it is
 * the column every listed object has, and the one that keeps the request valid.
 */
export const SearchFields = {
  advertisers: 'name;description;contact;email;legalName;url;externalReference',
  orders: 'name;externalReference',
  lineItems: 'name;externalReference;dealId',
  adUnits: 'name;description',
  sites: 'name;description;siteUrl',
  siteGroups: 'name;description',
  customEventTypes: 'name;description',
  creatives: 'name;description',
  layouts: 'name;description;externalReference',
  rulesets: 'name;description',
  blocklists: 'name;description',
  earningsAccounts: 'name;description;externalReference',
  teams: 'name;description;externalReference',
  teamGroups: 'name;description',
  roles: 'name;description',
  users: 'name;username;displayName;surname',
  politicalCampaigns: 'name;description',
  politicalEntities: 'name;description;legalEntity;contactEmail',
  axproduct: 'name;description;dealId',
  dataExports: 'name;description',
  emailTemplates: 'name;description',
  emailTranslations: 'name;description',
  reports: 'name',
  reportTemplates: 'name;description',
  reportSchedules: 'name',
  reportTranslations: 'name;description',
} as const

export type SearchableList = keyof typeof SearchFields

/**
 * Query parameters that narrow a list to the rows matching `search`. Returns
 * nothing when the search is empty, so it can be spread into any list request.
 */
export function searchParams(list: SearchableList, search?: string): Record<string, string> {
  const text = search?.trim()
  if (!text) return {}
  return { filterBy: SearchFields[list], filterByLike: text }
}

/** The list filter that finds the publishers, sites and ad units using a rule object. */
export type UsageParam = 'blocklist' | 'siteRuleset'

/** The publishers, sites and ad units that use a blocklist or ruleset. */
export interface ResourceUsage {
  publishers: { id: string; name: string }[]
  sites: { id: string; name: string }[]
  adUnits: { id: string; name: string }[]
}

/** Which object states a list includes. */
export type ObjectStatus = 'all' | 'ACTIVE' | 'INACTIVE'

/** Object-state parameters for a status filter. Missing means both states. */
export function statusParams(status: ObjectStatus = 'all'): Record<string, string> {
  return {
    includeActive: String(status !== 'INACTIVE'),
    includeInactive: String(status !== 'ACTIVE'),
  }
}
